import { ComponentsTypeEnum, FORMATS_DATE } from "@/constants";
import { useI18n } from "@/contexts/I18nContext";
import type { ProFormInstance } from "@ant-design/pro-components";
import {
  ProForm,
  ProFormSelect,
  ProFormText,
} from "@ant-design/pro-components";
import { Button, Flex } from "antd";
import dayjs from "dayjs";
import { useMemo, useRef, useState } from "react";

interface CustomFormProps {
  type: string;
  curMessage?: any;
  selectOptions?: any[];
  handleSendMessage: (params: any) => void;
}

const CustomForm = ({
  type,
  curMessage,
  selectOptions = [],
  handleSendMessage,
}: CustomFormProps) => {
  const formRef = useRef<ProFormInstance>(null);
  const typeTickForm = useRef<HTMLDivElement | null>(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const [hasSubmit, setHasSubmit] = useState(false);
  const { formatMessage } = useI18n();

  // 处理表单下拉数据
  const newSelectOptions = useMemo(() => {
    if (
      type === ComponentsTypeEnum.CANCEL_SUBSCRIPTION_PRODUCT_LIST &&
      selectOptions.length > 0
    ) {
      return selectOptions.map((option: any) => ({
        label: option.goods_name,
        value: option.plan_id,
      }));
    } else if (type === ComponentsTypeEnum.CANCEL_SUBSCRIPTION_PAUSE) {
      return selectOptions.map((option: any) => ({
        label: `${option}days`,
        value: option,
      }));
    }
    return [];
  }, [type, selectOptions]);

  // 处理表单数据
  const formatFormValues = (values: any) => {
    switch (type) {
      case ComponentsTypeEnum.EMAIL:
        return { question: values.email };
      case ComponentsTypeEnum.CODE:
        return { question: values.code };
      case ComponentsTypeEnum.CANCEL_SUBSCRIPTION_PRODUCT_LIST: {
        const curProductObj = selectOptions?.find(
          (option: any) => option.plan_id === values.plan_id,
        );
        if (curProductObj) {
          const {
            goods_name,
            pid,
            brand_id,
            unit_id,
            goods_id,
            currency,
            next_bill_time,
            status,
          } = curProductObj;
          return {
            question: goods_name,
            pid,
            brand_id,
            unit_id,
            currency,
            goods_id,
            plan_id: values.plan_id,
            next_bill_time,
            next_bill_date: dayjs(next_bill_time * 1000).format(
              FORMATS_DATE.DATE,
            ),
            status,
          };
        }
        return { question: "", plan_id: values.plan_id };
      }
      case ComponentsTypeEnum.CANCEL_SUBSCRIPTION_PAUSE: {
        const endDate = hasSubmit
          ? dayjs(
              (curMessage?.next_bill_time + values?.defer_time * 24 * 3600) *
                1000,
            ).format(FORMATS_DATE.DATE)
          : 0;
        return {
          question: hasSubmit
            ? formatMessage("submitText")
            : formatMessage("stillCancel"),
          defer_time: hasSubmit ? values?.defer_time : 0,
          next_bill_date: endDate,
        };
      }
      default:
        return {};
    }
  };

  // 渲染按钮
  const renderButtons = () => {
    if (type === ComponentsTypeEnum.CANCEL_SUBSCRIPTION_PAUSE) {
      return (
        <Flex vertical gap={12}>
          <Button
            type="primary"
            block
            shape="round"
            disabled={isDisabled}
            onClick={() => {
              setHasSubmit(true);
              formRef.current?.submit();
            }}
          >
            {formatMessage("submitText")}
          </Button>
          <Button
            type="default"
            block
            shape="round"
            disabled={isDisabled}
            onClick={() => {
              setHasSubmit(false);
              formRef.current?.submit();
            }}
          >
            {formatMessage("stillCancel")}
          </Button>
        </Flex>
      );
    }
    return (
      <Button type="primary" htmlType="submit" disabled={isDisabled}>
        {formatMessage("submitText")}
      </Button>
    );
  };

  // 渲染表单项
  const renderFormItems = () => {
    switch (type) {
      case ComponentsTypeEnum.EMAIL:
        return (
          <ProFormText
            name="email"
            label={formatMessage("emailLabel")}
            placeholder={formatMessage("emailPlaceholder")}
            rules={[
              { required: true, message: formatMessage("emailPlaceholder") },
              { type: "email", message: formatMessage("emailErrorText") },
            ]}
            fieldProps={{ disabled: isDisabled }}
          />
        );
      case ComponentsTypeEnum.CODE:
        return (
          <ProFormText
            name="code"
            label={formatMessage("codeLabel")}
            placeholder={formatMessage("codePlaceholder")}
            rules={[
              { required: true, message: formatMessage("codePlaceholder") },
            ]}
            fieldProps={{ disabled: isDisabled }}
          />
        );
      case ComponentsTypeEnum.CANCEL_SUBSCRIPTION_PRODUCT_LIST:
        return (
          <ProFormSelect
            name="plan_id"
            label={formatMessage("productLabel")}
            placeholder={formatMessage("productPlaceholder")}
            fieldProps={{
              showSearch: true,
              allowClear: true,
              options: newSelectOptions,
              getPopupContainer: () => typeTickForm.current || document.body,
            }}
            rules={[
              { required: true, message: formatMessage("productPlaceholder") },
            ]}
          />
        );
      case ComponentsTypeEnum.CANCEL_SUBSCRIPTION_PAUSE:
        return (
          <ProFormSelect
            name="defer_time"
            label={formatMessage("pauseDuration")}
            fieldProps={{
              showSearch: true,
              allowClear: true,
              options: newSelectOptions,
              getPopupContainer: () => typeTickForm.current || document.body,
            }}
            rules={[{ required: hasSubmit ? true : false }]}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div ref={typeTickForm}>
      <ProForm
        formRef={formRef}
        layout="vertical"
        submitter={{ render: () => renderButtons() }}
        onFinish={async (values) => {
          setIsDisabled(true);
          try {
            handleSendMessage(formatFormValues(values));
          } catch (error) {
            console.error("发送失败:", error);
          } finally {
            setIsDisabled(false);
          }
        }}
      >
        {renderFormItems()}
      </ProForm>
    </div>
  );
};

export default CustomForm;
